import React, { useState } from 'react';
import { useForm, Controller, useFieldArray } from 'react-hook-form';
import {
  Box, Button, MultiSelect, TextInput, Group, Text, Modal,
  Grid, ActionIcon, Card, Input, Title, Popover
} from '@mantine/core';
import { DatePicker } from '@mantine/dates';
import { MapPin, Calendar, Users, Info, Trash2, ChevronDown } from 'lucide-react';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import '@mantine/core/styles.css';
import '@mantine/dates/styles.css';
import GooglePlacesMultiSelect from './GooglePlacesMultiSelect';

// Extend dayjs for strict parsing
dayjs.extend(customParseFormat);

// --------------------------------------------------------
// CUSTOM COMPONENT: Strict Masked Date Input
// --------------------------------------------------------
const MaskedDateInput = ({ value, onChange, error, label, placeholder, description }) => {
  const [opened, setOpened] = useState(false);

  // Auto-format as MM/DD/YYYY while typing
  const handleTextChange = (e) => {
    let val = e.target.value.replace(/\D/g, ''); // Strip non-numeric
    if (val.length > 8) val = val.slice(0, 8);
    
    let formatted = val;
    if (val.length > 4) {
      formatted = `${val.slice(0, 2)}/${val.slice(2, 4)}/${val.slice(4, 8)}`;
    } else if (val.length > 2) {
      formatted = `${val.slice(0, 2)}/${val.slice(2, 4)}`;
    }
    
    onChange(formatted);
  };

  const parseDate = (str) => {
    if (!str || str.length < 10) return null;
    const dateObj = dayjs(str, 'MM/DD/YYYY');
    return dateObj.isValid() ? dateObj.toDate() : null;
  };

  const togglePopover = () => setOpened((o) => !o);

  return (
    <Popover opened={opened} onChange={setOpened} position="bottom-start" shadow="md">
      <Popover.Target>
        <TextInput
          label={label}
          description={description}
          placeholder={placeholder}
          value={value || ''}
          onChange={handleTextChange}
          error={error}
          radius="md"
          size="md"
          leftSection={
            <ActionIcon variant="transparent" onClick={togglePopover}>
              <Calendar size={18} color="#6c757d" />
            </ActionIcon>
          }
          rightSection={
            <ActionIcon variant="transparent" onClick={togglePopover}>
              <ChevronDown size={18} color="#6c757d" />
            </ActionIcon>
          }
        />
      </Popover.Target>
      <Popover.Dropdown>
        <DatePicker
          value={parseDate(value)}
          onChange={(val) => {
            if (val) {
              onChange(dayjs(val).format('MM/DD/YYYY'));
            } else {
              onChange('');
            }
            setOpened(false); // Close on selection
          }}
        />
      </Popover.Dropdown>
    </Popover>
  );
};

// Custom date parser to handle "12262026" -> 12/26/2026
const customDateParser = (input) => {
  const numericString = input.replace(/\D/g, '');
  if (numericString.length === 8) {
    const month = parseInt(numericString.substring(0, 2), 10);
    const day = parseInt(numericString.substring(2, 4), 10);
    const year = parseInt(numericString.substring(4, 8), 10);
    return new Date(year, month - 1, day);
  }
  return new Date(input);
};

const mockDestinations = [
  { value: 'spain', label: 'Spain' },
  { value: 'iowa', label: 'Iowa' },
  { value: 'saint_clair', label: 'Saint Clair, Pennsylvania' },
  { value: 'saint_marys', label: 'Saint Marys, Pennsylvania' },
  { value: 'sarver', label: 'Sarver, Pennsylvania' },
];

export default function TravelQuoteForm() {
  const [modalOpened, setModalOpened] = useState(false);

  // Initialize React Hook Form
  const { control, handleSubmit, formState: { errors }, watch,setValue,trigger } = useForm({
    mode: 'onChange', // Enables real-time validation errors
    defaultValues: {
      destinations: [],
      departureDate: null,
      returnDate: null,
      paymentDate: null,
      travelers: {
        zip: '', state: '',
        primary: { email: '', dob: null, age: '', cost: '' },
        companions: []
      }
    }
  });

  // Dynamic fields for companions
  const { fields: companions, append, remove } = useFieldArray({
    control,
    name: 'travelers.companions'
  });

  const onSubmit = (data) => {
    console.log("Form Submitted:", data);
  };


  // Label component with info icon
  const LabelWithInfo = ({ text }) => (
    <Group gap="xs" mb={4}>
      <Text size="sm" fw={700} c="#1a2a44">{text}</Text>
      <Info size={14} color="#888" style={{ cursor: 'pointer' }} />
    </Group>
  );

  // Helper to calculate age and update form state
  const handleDobChange = (val, fieldPath, ageFieldPath, rhfOnChange) => {
    rhfOnChange(val);
    if (val.length === 10) {
      const dateObj = dayjs(val, 'MM/DD/YYYY');
      if (dateObj.isValid()) {
        const calculatedAge = dayjs().diff(dateObj, 'year');
        setValue(ageFieldPath, calculatedAge.toString(), { shouldValidate: true });
        return;
      }
    }
    setValue(ageFieldPath, ''); // Reset if invalid/incomplete
  };

  // Validate Modal fields before closing
  const handleSaveModal = async () => {
    const fieldsToValidate = [
      'travelers.zip',
      'travelers.primary.email',
      'travelers.primary.dob',
      'travelers.primary.cost',
    ];
    companions.forEach((_, i) => {
      fieldsToValidate.push(`travelers.companions.${i}.dob`);
      fieldsToValidate.push(`travelers.companions.${i}.cost`);
    });

    const isValid = await trigger(fieldsToValidate);
    if (isValid) {
      setModalOpened(false);
    }
  };

  return (
  
    <>
    
  <div className="quote-card">
          <h3>Help us choose the right plan for you</h3>
        <form onSubmit={handleSubmit(onSubmit)}>
          
          {/* DESTINATIONS (MultiSelect) */}
          <Box mb="md">
            <Controller
              name="destinations"
              control={control}
              rules={{ required: 'Destination is required' }}
              render={({ field }) => (
               <GooglePlacesMultiSelect
        value={field.value}
        onChange={field.onChange}
        label={<LabelWithInfo text="Destinations" />}
        error={errors.destinations?.message}
      />
              )}
            />
          </Box>

         

          {/* DATES ROW */}
          <Grid mb="md">
            <Grid.Col span={6}>
              <Controller
                name="departureDate"
                control={control}
               rules={{ 
                  required: 'Departure date is required',
                  pattern: { value: /^\d{2}\/\d{2}\/\d{4}$/, message: 'Invalid format' }
                }}
                render={({ field }) => (
                  <MaskedDateInput
                    {...field}
                    label={<LabelWithInfo text="Departure Date" />}
                    placeholder="MM/DD/YYYY"
                    error={errors.departureDate?.message}
                  />
                )}
              />
            </Grid.Col>

            <Grid.Col span={6}>
              <Controller
                name="returnDate"
                control={control}
                render={({ field }) => (
                  <MaskedDateInput
                    {...field}
                    label={<LabelWithInfo text="Return Date" />}
                    placeholder="MM/DD/YYYY"
                    error={errors.returnDate?.message}
                  />
                )}
              />
            </Grid.Col>
          </Grid>

           <Box mb='md'>
             <Controller
                name="paymentDate"
                control={control}
                render={({ field }) => (
                  <MaskedDateInput
                    {...field}
                    label={<LabelWithInfo text="Initial Trip Payment Date" />}
                    placeholder="MM/DD/YYYY"
                    error={errors.paymentDate?.message}
                  />
                )}
              />
          </Box>

          {/* TRAVELER DETAILS (Triggers Modal) */}
          <Box mb="xl">
            <Input.Wrapper label={<LabelWithInfo text="Traveler Details & Trip Cost" />}>
              <Input
                component="button"
                type="button"
                pointer
                onClick={() => setModalOpened(true)}
                leftSection={<Users size={18} color="#6c757d" />}
                rightSection={<ChevronDown size={18} color="#6c757d" />}
                radius="md"
                size="md"
                styles={{ input: { textAlign: 'left', color: '#495057' } }}
              >
                {companions.length + 1} Traveler(s) Added
              </Input>
            </Input.Wrapper>
          </Box>

          <Button type="submit" fullWidth size="md" color="#0056b3" radius="md">
            Get a Quote
          </Button>
      </form>
      
      </div>
      


      {/* TRAVELER DETAILS MODAL */}
      {/* MODAL */}
      <Modal
        opened={modalOpened}
        onClose={() => setModalOpened(false)}
        title={<Text fw={700} size="lg" c="white">Traveler Details & Trip Cost</Text>}
        size="xl" centered
        styles={{
          header: { backgroundColor: '#1e2b4d', padding: '16px 24px' },
          close: { color: 'white' },
          body: { padding: '24px' }
        }}
      >
        <Box>
          <Text fw={600} c="#0056b3" mb="xs">State of Residence</Text>
          <Grid mb="xl" align='flex-end'>
            <Grid.Col span={4}>
              <Controller
                name="travelers.zip"
                control={control}
                rules={{ required: 'Required' }}
                render={({ field }) => <TextInput {...field} placeholder="Zip Code" description="Required" error={errors.travelers?.zip?.message} />}
              />
            </Grid.Col>
            <Grid.Col span={8}>
              <TextInput placeholder="State" disabled bg="#f1f3f5" />
            </Grid.Col>
          </Grid>

          <Text fw={600} c="#0056b3" mb="xs">Primary Traveler</Text>
          <Grid mb="xl" align='flex-end'>
            <Grid.Col span={4}>
              <Controller
                name="travelers.primary.email"
                control={control}
                rules={{ required: 'Required', pattern: /^\S+@\S+$/i }}
                render={({ field }) => <TextInput {...field} placeholder="Email" description="Required" error={!!errors.travelers?.primary?.email} />}
              />
            </Grid.Col>
            <Grid.Col span={3}>
              <Controller
                name="travelers.primary.dob"
                control={control}
                rules={{ required: 'Required', pattern: /^\d{2}\/\d{2}\/\d{4}$/ }}
                render={({ field }) => (
                  <MaskedDateInput
                    {...field}
                    onChange={(val) => handleDobChange(val, 'travelers.primary.dob', 'travelers.primary.age', field.onChange)}
                    placeholder="DOB" description="Required" error={!!errors.travelers?.primary?.dob}
                  />
                )}
              />
            </Grid.Col>
            <Grid.Col span={2}>
              <Controller name="travelers.primary.age" control={control} render={({ field }) => <TextInput {...field} placeholder="Age" readOnly bg="#f8f9fa" />} />
            </Grid.Col>
            <Grid.Col span={3}>
              <Controller
                name="travelers.primary.cost"
                control={control}
                rules={{ required: 'Required' }}
                render={({ field }) => <TextInput {...field} placeholder="Trip Cost" rightSection={<Info size={14} color="#888"/>} description="Required" error={!!errors.travelers?.primary?.cost} />}
              />
            </Grid.Col>
          </Grid>

          {companions.map((companion, index) => (
            <Box key={companion.id} mb="md">
              <Text fw={600} c="#0056b3" mb="xs">Companion {index + 1}</Text>
              <Grid align='flex-end'>
                <Grid.Col span={3}>
                  <Controller
                    name={`travelers.companions.${index}.dob`}
                    control={control}
                    rules={{ required: 'Required', pattern: /^\d{2}\/\d{2}\/\d{4}$/ }}
                    render={({ field }) => (
                      <MaskedDateInput
                        {...field}
                        onChange={(val) => handleDobChange(val, `travelers.companions.${index}.dob`, `travelers.companions.${index}.age`, field.onChange)}
                        placeholder="DOB" description="Required" error={!!errors.travelers?.companions?.[index]?.dob}
                      />
                    )}
                  />
                </Grid.Col>
                <Grid.Col span={2}>
                  <Controller name={`travelers.companions.${index}.age`} control={control} render={({ field }) => <TextInput {...field} placeholder="Age" readOnly bg="#f8f9fa" />} />
                </Grid.Col>
                <Grid.Col span={3}>
                  <Controller
                    name={`travelers.companions.${index}.cost`}
                    control={control}
                    rules={{ required: 'Required' }}
                    render={({ field }) => <TextInput {...field} placeholder="Trip Cost" rightSection={<Info size={14} color="#888"/>} description="Required" error={!!errors.travelers?.companions?.[index]?.cost} />}
                  />
                </Grid.Col>
                <Grid.Col span={2}>
                  <ActionIcon color="red" variant="subtle" onClick={() => remove(index)} mt="4px">
                    <Trash2 size={20} />
                  </ActionIcon>
                </Grid.Col>
              </Grid>
            </Box>
          ))}

          <Group justify="space-between" mt="xl" pt="md" style={{ borderTop: '1px solid #eee' }}>
            <Button variant="outline" color="#0056b3" onClick={() => append({ dob: '', age: '', cost: '' })}>
              + Add Companion
            </Button>
            <Button color="#0056b3" onClick={handleSaveModal}>
              Save
            </Button>
          </Group>
        </Box>
      </Modal>

      </>

   
  );
}